import { IPRegion, PageRegion, PageRegionalUrls } from "@/app/settings";
import { useCookies } from "@vueuse/integrations/useCookies";
import { ref } from "vue";
import { defaultLocale, i18n } from "../app/i18n";

/**
 * Typealias for country code (e.g. "uk", "us").
 */
export type CountryCode = "uk" | "us" | "au" | "za" | string;

/**
 * Regional data object.
 *
 * This objects are defined in "@/assets/regions.json".
 */
export type Region = {
  country_code: string;
  country_name: string;
  page: string;
  flag: { src: string; alt: string };
  spirit_country_code?: string;
  // excludeRoles?: RoleType[];
};

const defaultRegionCode = "uk";
const regionCookieId = "region";
const regions: Record<string, Region> = window.regionSettings.reduce(
  (o, v) => ({ ...o, [v.value.country_code]: v.value }),
  {}
);
const defaultRegion = regions[defaultRegionCode];
const currentRegion = ref<Region>(defaultRegion);

function getRegionCookie() {
  const cookies = useCookies();
  return cookies.get<CountryCode | undefined>(regionCookieId);
}

function setRegionCookie(code: CountryCode) {
  const cookies = useCookies();
  cookies.set(regionCookieId, code, { path: "/" });
}

/**
 * Setup region cookie, app locale and redirect if necessary.
 * To run once before the app.
 */
function setupLocale() {
  // 1. Get region from cookies.
  let userRegion = getRegionCookie();

  // 2. If no region cookie, use region from user IP or default.
  if (!userRegion) {
    const finalRegion = IPRegion ?? defaultRegionCode;
    setRegionCookie(finalRegion);
    userRegion = finalRegion;
  }

  // 3. If the current page is on the wrong region try to redirect,
  //    otherwise replace the user region with the current page region.
  if (userRegion !== PageRegion) {
    if (PageRegionalUrls?.[userRegion]) {
      document.location.href = PageRegionalUrls[userRegion];
    } else if (PageRegion !== "uk") {
      userRegion = PageRegion;
    }
  }

  // 4. Assign locale based on current region.
  switch (userRegion) {
    case "uk":
      i18n.global.locale = "en-GB";
      break;

    case "au":
      i18n.global.locale = "en-AU";
      break;

    case "za":
      i18n.global.locale = "en-ZA";
      break;

    default:
      i18n.global.locale = defaultLocale;
      break;
  }

  currentRegion.value = regions[userRegion] ?? defaultRegion;
}

/**
 * Return the current region.
 *
 * @returns
 */
function getSelectedRegion(): Region {
  return currentRegion.value;
}

/**
 * Get the Region by the given link.
 *
 * @param link
 * @returns
 */
function getRegionFromLink(link: string): Region {
  const result = Object.values(regions).find((r) => {
    const reg = new RegExp("^/" + r.country_code + "/");
    return reg.test(link);
  });
  return result || defaultRegion;
}

/**
 * Add the current regional path to the given URL string.
 *
 * @param url
 * @returns
 */
function addRegionToLink(url: string): string {
  if (url.indexOf(currentRegion.value.page) !== 0) {
    return currentRegion.value.page + url;
  } else {
    return url;
  }
}

/**
 * Redirect to the given region.
 *
 * @param region
 */
function navigateToRegion(code: string) {
  if (regions[code]) {
    console.log("navigation to region", code);
    setRegionCookie(code);
    document.location.href = regions[code].page;
  }
}

const RegionHelper = {
  currentRegion,
  setupLocale,
  getRegions: () => regions,
  getSelectedRegion,
  getRegionFromLink,
  addRegionToLink,
  navigateToRegion,
};

// Composition API style export.
// Preferred as it reduces makes clear where the dependency is used.
export const useRegionHelper = () => RegionHelper;

export { RegionHelper };
